import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { makeApiCall } from "src/api/apiRequests";
import { useNavigate } from "react-router-dom";
import { PermissionCheck } from "src/helpers/PermissionCheck";
import ReactChart from "src/components/common/ReactChart";
import FLASH_MESSAGES from "src/helpers/FlashMessages";
import API_HEADERS from "src/api/apiConfig";
import API_PATHS from "src/api/apiPaths";
import Loading from "src/helpers/Loading";

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [totalChildren, setTotalChildren] = useState<number>(0);
  const [childrenGender, setChildrenGender] = useState<any>({});
  const [totalKindergartens, setTotalKindergartens] = useState<number>(0);
  const [totalNurseries, setTotalNurseries] = useState<number>(0);
  const [totalChildrenAgeGroup, setTotalChildrenAgeGroup] = useState<any>({});
  const [totalMembers, setTotalMembers] = useState<any>({});
  const [dashboardLoading, setDashboardLoading] = useState<boolean>(true);
  const [kindergartensWithChildren, setKindergartensWithChildren] = useState(
    []
  );
  const [nurseriesWithChildrens, setNurseriesWithChildrens] = useState([]);

  const [showAllKindergartens, setShowAllKindergartens] = useState(false);
  const [showAllNurseries, setShowAllNurseries] = useState(false);

  const fetchReport = async (): Promise<void> => {
    try {
      const response: any = await makeApiCall<ResponseType>(
        API_PATHS.adminDashboard,
        "GET",
        API_HEADERS.authenticated
      );
 
      setTotalChildren(response.total_children);
      setChildrenGender(response.children_gender);
      setTotalKindergartens(response.total_kindergartens);
      setTotalNurseries(response.total_nurseries);
      setTotalChildrenAgeGroup(response.age_group_children);
      setTotalMembers(response.total_members_nr);
      setDashboardLoading(false);
      setKindergartensWithChildren(response.kindergartens_with_children_count);
      setNurseriesWithChildrens(response.nurseries_with_children_count);
    } catch (error: any) {
      if (error.response.status === 404) {
        FLASH_MESSAGES.errorMsg(error.response.data.error);
        navigate(-1);
      }
    }
  };

  useEffect(() => {
    PermissionCheck("dashboard-all-children") && fetchReport();
  }, []);

  const formattedTotalMembers =
    Array.isArray(totalMembers) && totalMembers.length > 0
      ? totalMembers
      : [{ name: "No Data", users_count: 0 }];

  const totalChildrenKindergartens = kindergartensWithChildren.reduce(
    (acc: number, child: any) => acc + (child.active_children_count || 0),
    0
  );
  const totalChildrenNurseries = nurseriesWithChildrens.reduce(
    (acc: number, child: any) => acc + (child.active_children_count || 0),
    0
  );

  const displayedNurseries = showAllNurseries
    ? nurseriesWithChildrens
    : nurseriesWithChildrens.slice(0, 10);
  const displayedKindergartens = showAllKindergartens
    ? kindergartensWithChildren
    : kindergartensWithChildren.slice(0, 10);

  return (
    <>
      {dashboardLoading ? (
        <div className="text-center">
          <Loading />
        </div>
      ) : (
        <>
          <div className="row">
            <div className="col-12 col-sm-4">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="general-info">
                    <span className="gen-title">
                      {t("dashboard_total_number_of_children")}
                    </span>
                    <span className="info">{totalChildren}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-4">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="general-info">
                    <span className="gen-title">
                      {t("dashboard_total_number_of_nurseries")}
                    </span>
                    <span className="info">{totalNurseries}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-sm-4">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="general-info">
                    <span className="gen-title">
                      {t("dashboard_total_number_of_kindergardens")}
                    </span>
                    <span className="info">{totalKindergartens}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-lg-4">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="card-title mb-5">
                    {t("dashboard_children_gender_pie")}
                  </div>
                  <ReactChart
                    type="donut"
                    series={[childrenGender.boys, childrenGender.girls]}
                    otherOptions={{
                      legend: {
                        position: "bottom",
                      },
                      labels: [
                        t("dashboard_children_gender_boys"),
                        t("dashboard_children_gender_girls"),
                      ],
                      colors: ["#3d6de0", "#ab56d4"],
                      chart: {
                        height: 470,
                        id: "basic-bar",
                      },
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-4">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="card-title mb-5">
                    {t("dashboard_children_age_goups_number")}
                  </div>
                  <ReactChart
                    type="donut"
                    series={[
                      totalChildrenAgeGroup["6-8"],
                      totalChildrenAgeGroup["8-10"],
                      totalChildrenAgeGroup["10-12"],
                      totalChildrenAgeGroup["12-36"],
                      totalChildrenAgeGroup["36-72"],
                    ]}
                    otherOptions={{
                      legend: {
                        position: "bottom",
                      },
                      labels: [
                        `6-8 ${t("dashboard_children_age_goup_months")}`,
                        `8-10 ${t("dashboard_children_age_goup_months")}`,
                        `10-12 ${t("dashboard_children_age_goup_months")}`,
                        `1-3 ${t("dashboard_children_age_goup_years")}`,
                        `3-6 ${t("dashboard_children_age_goup_years")}`,
                      ],
                      colors: [
                        "#6592F3",
                        "#EF8559",
                        "#EEC75C",
                        "#80E4B6",
                        "#9386F0",
                      ],
                      chart: {
                        height: 460,
                        id: "basic-bar",
                      },
                    }}
                  />
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-4 ">
              <div className="card second mb-4">
                <div className="card-body">
                  <div className="card-title">
                    {t("dashboard_number_of_staff")}
                  </div>
                  <div className="row">
                    <ReactChart
                      type="bar"
                      series={[
                        {
                          name: "Total",
                          color: "#3d6de0",
                          data: formattedTotalMembers.map(
                            (member: any) => member.users_count || 0
                          ), // Ensure data is numerical
                        },
                      ]}
                      otherOptions={{
                        chart: {
                          id: "basic-bar",
                        },
                        dataLabels: {
                          enabled: false,
                        },
                        legend: {
                          show: false,
                        },
                        xaxis: {
                          categories: formattedTotalMembers.map(
                            (member: any) => member.name || "Unknown"
                          ), // Ensure categories are strings
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-lg-6">
              <div className="card second mb-4">
                <div className="card-body">
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div className="card-title">
                      {t("dashboard_children_number_kindergarten")} : {""}
                      <strong>{totalChildrenKindergartens}</strong>
                    </div>
                    <div>
                      <button
                        className="button-white "
                        onClick={() =>
                          setShowAllKindergartens(!showAllKindergartens)
                        }
                      >
                        {showAllKindergartens ? t("show_less") : t("show_all")}
                      </button>
                    </div>
                  </div>

                  <ReactChart
                    type="bar"
                    series={[
                      {
                        color: "#3d6de0",
                        data: displayedKindergartens.map(
                          (child: any) => child.active_children_count || 0,
                          0
                        ),
                        name: t("children_count"),
                      },
                    ]}
                    otherOptions={{
                      labels: displayedKindergartens.map(
                        (item: any) => item.name
                      ),
                      legend: {
                        position: "bottom",
                      },

                      chart: {
                        id: "nursery-bar",
                      },

                      xaxis: {
                        categories: displayedKindergartens.map(
                          (item: any) => item.name
                        ),
                      },
                      grid: {
                        padding: {
                          bottom: 20,
                        },
                      },
                    }}
                    height={600}
                  />
                </div>
              </div>
            </div>

            <div className="col-12 col-lg-6">
              <div className="card second mb-4">
                <div className="card-body">
                  <div
                    style={{ display: "flex", justifyContent: "space-between" }}
                  >
                    <div className="card-title">
                      {t("dashboard_children_number_nurseries")} :{" "}
                      {<strong>{totalChildrenNurseries}</strong>}
                    </div>
                    <button
                      className="button-white"
                      onClick={() => setShowAllNurseries(!showAllNurseries)}
                    >
                      {showAllNurseries ? t("show_less") : t("show_all")}
                    </button>
                  </div>
                  <ReactChart
                    type="bar"
                    series={[
                      {
                        color: "#3d6de0",
                        data: displayedNurseries.map(
                          (child: any) => child.active_children_count || 0,
                          0
                        ),
                        name: t("children_count"),
                      },
                    ]}
                    otherOptions={{
                      labels: displayedNurseries.map((item: any) => item.name),
                      legend: {
                        position: "bottom",
                      },

                      chart: {
                        id: "nursery-bar",
                      },

                      xaxis: {
                        categories: displayedNurseries.map(
                          (item: any) => item.name
                        ),
                      },
                      grid: {
                        padding: {
                          bottom: 20,
                        },
                      },
                    }}
                    height={450}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default AdminDashboard;
